import { buildCollection } from "@firecms/core";

import { MarkdownField } from "../fields/MarkdownField";
import { PostImagesField } from "../fields/PostImagesField";
import { unsavedUploads } from "../fields/unsavedUploads";
import { buildSearchTokens } from "./searchTokens";
import { cleanUpReplacedUploads } from "./uploadCleanup";

export type BlogPost = {
  title: string;
  date: Date;
  body: string;
  images?: string[];
  searchTokens?: string[];
};

// Mirrors the `blogPosts` schema and its validator in firestore.rules.
// `images` and `searchTokens` are CMS bookkeeping; the site only reads title,
// date and body. No draft flag: a post is live once saved, unless future-dated.
export const blogPostsCollection = buildCollection<BlogPost>({
  name: "Blog",
  singularName: "Blog post",
  id: "blogPosts",
  path: "blogPosts",
  icon: "Article",
  group: "Content",
  description: "Blog posts, shown newest first on the Blog page.",
  initialSort: ["date", "desc"],
  // Full screen gives the editor and its preview room.
  openEntityMode: "full_screen",
  callbacks: {
    ...cleanUpReplacedUploads,
    onSaveSuccess(props) {
      // Now owned by the saved post.
      (props.values.images ?? []).forEach((url) => unsavedUploads.delete(url));
      return cleanUpReplacedUploads.onSaveSuccess?.(props);
    },
    onPreSave({ values }) {
      return { ...values, searchTokens: buildSearchTokens(values.title ?? "", values.body ?? "") };
    },
  },
  properties: {
    title: {
      name: "Title",
      dataType: "string",
      validation: { required: true, max: 200 },
    },
    date: {
      name: "Post date",
      dataType: "date",
      mode: "date",
      validation: { required: true },
      description:
        "Shown on the post, and what the blog is sorted by, newest first. A post dated in the future stays off the site until that day.",
    },
    body: {
      name: "Post",
      dataType: "string",
      Field: MarkdownField,
      validation: { required: true, max: 200000 },
    },
    images: {
      name: "Photos for this post",
      dataType: "array",
      of: { dataType: "string" },
      Field: PostImagesField,
      validation: { max: 50 },
      description:
        "Upload a photo, then use “Add to post” or copy its link into the post's image button. Photos removed here are deleted for good when the post is saved.",
    },
    // Hidden; written by onPreSave.
    searchTokens: {
      name: "Search terms",
      dataType: "array",
      of: { dataType: "string" },
      disabled: { hidden: true },
      hideFromCollection: true,
    },
  },
});
